import { useEffect, useState } from 'react'
import {
  Alert,
  Breadcrumbs,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Link,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { TreeItem, TreeItemProps } from '@mui/x-tree-view'
import { RichTreeView } from '@mui/x-tree-view/RichTreeView'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels'
import {
  findNodeById,
  getParentNode,
  isChildrenLoaded,
  isPlaceholderNode,
  isShareableLocation,
  ParentIds,
  TreeNode,
} from './treeModel'
import { sendShareDocumentRequest } from './shareApi'
import { useMessageState } from './messageState'
import { useBreadcrumbsState } from './breadcrumbsState'
import { useTreeState } from './treeState'

const STACK_SPACING = 2

export interface SharePointTreeViewProps {
  docId: string
  snapshotId: string
  filename: string
  container?: ShadowRoot
  onDone?: (isOpenFileInSharePoint: boolean) => void
  onCancel?: () => void
}

const SharePointTreeView: React.FC<SharePointTreeViewProps> = (
  props: SharePointTreeViewProps
) => {
  const [filename, setFilename] = useState<string>(props.filename)
  const [isConfirmOpen, setConfirmOpen] = useState<boolean>(false)
  const [isSharing, setSharing] = useState<boolean>(false)
  const [isOpenFileInSharePoint, setOpenFileInSharePoint] = useState<boolean>(
    localStorage.getItem('isOpenFileInSharePoint') === 'true'
  )

  const treeState = useTreeState()
  const breadcrumbs = useBreadcrumbsState(treeState.treeData, treeState.selectedNode)
  const messageState = useMessageState()

  useEffect(() => {
    if (treeState.error && treeState.error !== messageState.message) {
      messageState.showError(treeState.error)
    }
  }, [treeState.error, treeState.reconcileCounter])

  const handleSelectedItemsChange = (
    event: React.SyntheticEvent | null,
    nodeId: string | null
  ) => {
    messageState.hide()
    treeState.selectNode(nodeId)
  }

  const flattenNodes = (children: TreeNode[]): TreeNode[] => {
    const nodes = children.filter(
      (node: TreeNode) => !isPlaceholderNode(node)
    )
    return nodes.map((node: TreeNode) => {
      const flattenedNode = {...node, hasChildren: false, children: []}
      return flattenedNode
    })
  }

  const filterOutFiles = (nodes: TreeNode[]): TreeNode[] => {
    const result: TreeNode[] = []
    for (const node of nodes) {
      if (node.type === 'file') continue
      const nodeCopy: TreeNode = {...node}
      if (nodeCopy.children) {
        nodeCopy.children = filterOutFiles(nodeCopy.children)
      }
      result.push(nodeCopy)
    }
    return result
  }

  const selectedNodeParent: TreeNode | null = (
    treeState.selectedNode
    && getParentNode(treeState.treeData, treeState.selectedNode)
    || null
  )
  const selectedNodeParentAlias: TreeNode | null = (
    selectedNodeParent
    ? {...selectedNodeParent, label: "..", hasChildren: false, children: []}
    : null
  )
  const filteredTreeData = filterOutFiles(treeState.treeData)
  const selectedFolderNodes = (
    treeState.selectedNode && treeState.selectedNode.children
    ? (
      selectedNodeParentAlias
      ? [selectedNodeParentAlias, ...flattenNodes(treeState.selectedNode.children)]
      : flattenNodes(treeState.selectedNode.children)
    )
    : (
      selectedNodeParentAlias
      ? [selectedNodeParentAlias]
      : []
    )
  )

  const handleBreadcrumbClick = (event: React.SyntheticEvent, nodeId: string) => {
    event.stopPropagation()
    event.preventDefault()
    treeState.selectNode(nodeId)
  }

  const isSelectedNodeChildrenLoading: boolean = !!(
    treeState.selectedNode?.children?.length
    && isPlaceholderNode(treeState.selectedNode.children[0])
  )

  const handleExpandedItemsChange = (
    event: React.SyntheticEvent | null,
    newExpandedNodeIds: string[]
  ) => {
    treeState.setExpandedNodeIds(newExpandedNodeIds)
  }

  const isFilenameExists = (value: string): boolean => {
    for (const node of selectedFolderNodes) {
      if (node.label.toLowerCase() === value.toLowerCase()) return true
    }
    return false
  }

  const showConfirm = () => {
    setConfirmOpen(true)
  }

  const hideConfirm = () => {
    setConfirmOpen(false)
  }

  const share = async () => {
    startSharing()
    shareDocument()
  }

  const shareDocument = async (): Promise<void> => {
    if (!treeState.selectedNode) {
      messageState.showError("Sharing location is not selected")
      return Promise.resolve()
    }

    sendShareDocumentRequest(
      props.docId,
      props.snapshotId,
      filename,
      makeParentIdsForChildren(treeState.selectedNode)
    )
      .then(() => {
        messageState.showSuccess("Document shared successfully")
        props.onDone?.(isOpenFileInSharePoint)
      })
      .catch((err: Error) => {
        messageState.showError(err.message || "Error happened while sharing document")
      })
      .finally(() => {
        stopSharing()
      })
  }

  const makeParentIdsForChildren = (node: TreeNode): ParentIds => {
    switch (node.type) {
      case 'site':
        return { siteId: node.id } as ParentIds
      case 'drive':
        return {
          siteId: node.parentIds.siteId,
          driveId: node.id
        } as ParentIds
      case 'folder':
        return {
          siteId: node.parentIds.siteId,
          driveId: node.parentIds.driveId,
          itemId: node.id
        } as ParentIds
    }

    throw `Node type ${node.type} can't have child nodes`
  }

  const handleShare = async (event: React.SyntheticEvent) => {
    if (!treeState.selectedNode) {
      messageState.showError("Select a drive or a folder to share document to")
      return
    }

    if (!isShareableLocation(treeState.selectedNode)) {
      messageState.showError("You can upload documents only to drives and folders")
      return
    }

    if (isFilenameExists(filename)) {
      showConfirm()
      return
    }

    await share()
  }

  const handleCancel = (event: React.SyntheticEvent) => {
    props.onCancel?.()
  }

  const startSharing = () => {
    setSharing(true)
  }

  const stopSharing = () => {
    setSharing(false)
  }

  const handleConfirmShareAndOverwrite = async () => {
    await share().finally(hideConfirm)
  }

  const handleConfirmRejectShareAndOverwrite = () => {
    hideConfirm()
  }

  const canShare = !!(
    treeState.selectedNode
    && isShareableLocation(treeState.selectedNode)
    && isChildrenLoaded(treeState.selectedNode)
  )

  const onOpenFileInSharePointChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked
    localStorage.setItem('isOpenFileInSharePoint', String(isChecked))
    setOpenFileInSharePoint(isChecked)
  }

  let shareConditions: string = ""
  if (!treeState.selectedNode) {
    shareConditions = "Select a drive or a folder to share document to"
  } else {
    if (isSelectedNodeChildrenLoading) {
      shareConditions = "Loading..."
    } else if (!isShareableLocation(treeState.selectedNode)) {
      if (treeState.selectedNode.type === 'site') {
        shareConditions = "You can't share to a site, select a drive or a folder"
      } else {
        shareConditions = "Select a drive or a folder to share document to"
      }
    }
  }

  const canRefresh = !!(treeState.selectedNode && !isSelectedNodeChildrenLoading)

  const handleRefreshSelectedNode = () => {
    if (!canRefresh || !treeState.selectedNode) return
    treeState.refreshChildren(treeState.selectedNode.id)
  }

  const handleSelectNode = (itemId: string) => {
    const node = findNodeById(treeState.treeData, itemId)
    if (!node) return
    if (node.type !== 'file') return
    setFilename(node.label)
  }

  const theme = createTheme({
    components: {
      MuiModal: {
        defaultProps: {
          container: props.container?.host as Element, // shadowRoot for all nested components
        },
      },
      // This fixes tooltip visibility when displayed inside Shadow DOM
      MuiTooltip: {
        defaultProps: {
          slotProps: {
            popper: {
              container: props.container?.host as Element,
              disablePortal: true,
            },
          },
        },
      },
    },
  })

  return (
    <ThemeProvider theme={theme}>
      <Snackbar
        open={messageState.isVisible}
        autoHideDuration={5000}
        onClose={messageState.hide}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={messageState.hide} severity={messageState.severity} variant="filled">
          {messageState.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={isConfirmOpen}
        onClose={() => !isSharing && hideConfirm()}
        container={props.container?.host as Element}
        disablePortal
        disableScrollLock
      >
        <DialogTitle>Confirm Overwriting</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to overwrite the existing SharePoint document named: {filename}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmRejectShareAndOverwrite}
            disabled={isSharing}
          >
            No
          </Button>
          <Button
            onClick={handleConfirmShareAndOverwrite}
            color="error"
            variant="contained"
            disabled={isSharing}
            startIcon={isSharing && <CircularProgress size={18} />}
          >
            {isSharing ? "Sharing..." : "Share & Overwrite"}
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          height: "550px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Splitter Section */}
        <PanelGroup
          direction="horizontal"
          style={{ height: "100%" }}
        >
          {/* Left Panel */}
          <Panel
            defaultSize={50}
            minSize={10}
            style={{ overflow: "auto", paddingBottom: 8 }}
          >
            <Box
              sx={{
                bgcolor: "white",
                height: "100%",
                display: "flex",
                pt: 1,
                pr: 1,
                pb: 2,
                pl: 1,
                overflow: "auto",
              }}
            >
              <RichTreeView<TreeNode>
                items={filteredTreeData}
                onSelectedItemsChange={handleSelectedItemsChange}
                expansionTrigger="iconContainer"
                selectedItems={treeState.selectedNode ? treeState.selectedNode.id : ""}
                expandedItems={treeState.expandedNodeIds}
                onExpandedItemsChange={handleExpandedItemsChange}
                sx={[
                  {
                    // Target all tree item labels
                    '& .MuiTreeItem-label': {
                      wordBreak: 'break-all',      // force wrap anywhere
                      overflowWrap: 'break-word',  // optional, more readable
                      whiteSpace: 'normal',        // allow wrapping instead of single line
                    },
                  },
                  isSharing && {
                    pointerEvents: "none",
                    opacity: 0.6
                  },
                  {
                    width: "100%"
                  }
                ]}
              />
            </Box>
          </Panel>

          <PanelResizeHandle
            style={{
              width: "4px",
              cursor: "col-resize",
              backgroundColor: "#ccc",
            }}
          />

          {/* Right Panel */}
          <Panel
            defaultSize={50}
            minSize={10}
            style={{ overflow: "auto", paddingBottom: 8 }}
          >
            <Box
              sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                p: 1,
                overflow: "auto",
                bgcolor: "grey.100",
              }}
            >
              <Stack direction="row" sx={{
                width: "100%",
              }}>
                { treeState.selectedNode
                  ? (
                    isSelectedNodeChildrenLoading
                    ? <Typography sx={{ padding: '4px 8px' }}>Loading...</Typography>
                    : <RichTreeView<TreeNode>
                      key={`children-${treeState.reconcileCounter}`}
                      items={selectedFolderNodes}
                      sx={[
                        {
                          flexGrow: 1,
                          '& .MuiTreeItem-label': {
                            wordBreak: 'break-all',      // force wrap anywhere
                            overflowWrap: 'break-word',  // optional, more readable
                            whiteSpace: 'normal',        // allow wrapping instead of single line
                          },
                        },
                        isSharing && {
                          pointerEvents: "none",
                          opacity: 0.6
                        },
                      ]}
                      slots={{
                        item: (props) => (
                          <CustomTreeItem
                            onItemClick={handleSelectNode}
                            onItemDoubleClick={treeState.selectNode}
                            {...props}
                          />
                        )
                      }}
                    />
                  )
                  : ""
                }
                { canRefresh
                  ? <IconButton
                    sx={{
                      p: 0.5,
                      marginRight: 2,
                      alignSelf: 'flex-start',
                      backgroundColor: 'transparent',
                    }}
                    onClick={handleRefreshSelectedNode}
                  >
                    <RefreshIcon />
                  </IconButton>
                  : ""
                }
              </Stack>
            </Box>
          </Panel>
        </PanelGroup>

        {/* Control Panel Pinned to Bottom */}
        <Paper
          elevation={3}
          sx={{
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            position: "sticky",
            bottom: 0,
          }}
        >
          <Stack spacing={STACK_SPACING}>
            { breadcrumbs.length ? <>
              <Breadcrumbs
                aria-label="breadcrumb"
                sx={{
                  '& .MuiBreadcrumbs-separator': {
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiTypography-root, & .MuiLink-root': {
                    whiteSpace: 'normal',      // allow wrapping
                    wordBreak: 'break-all',    // force break inside long strings
                    overflowWrap: 'break-word' // optional, more readable
                  },
                  '& .MuiBreadcrumbs-li:last-child': {
                    '& .MuiTypography-root::after': {
                      content: '":"',
                      marginLeft: '4px',
                    }
                  },
                }}
              >
                { breadcrumbs.map((node: TreeNode, index: number) => {
                  const isLast = index === breadcrumbs.length - 1
                  const label = node.label
                  return isLast ? (
                    <Typography sx={{ color: 'text.primary' }}>{label}</Typography>
                  ) : (
                    <Link
                      key={index}
                      underline="hover"
                      color="inherit"
                      href="#"
                      onClick={(event) => handleBreadcrumbClick(event, node.id)}
                    >
                      {label}
                    </Link>
                  )
                }) }
              </Breadcrumbs>
            </> : <Typography sx={{ color: 'text.primary' }}>
              Select location to share document to.
            </Typography> }
            <TextField
              id="outlined-basic"
              label="Target filename"
              variant="outlined"
              value={filename}
              disabled={isSharing}
              onChange={e => setFilename(e.target.value)}
            />
            <Box
              sx={{
                display: "flex",
                width: "100%",
                justifyContent: "space-between",
                alignItems: "center"
              }}
            >
              <Box sx={{ display: "flex", width: "100%" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isOpenFileInSharePoint}
                      onChange={onOpenFileInSharePointChange}
                    />
                  }
                  label="Open file in SharePoint after sharing"
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, width: "100%" }}>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={isSharing}
                >
                  Cancel
                </Button>
                <Tooltip title={shareConditions}>
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleShare}
                      disabled={!canShare || isSharing}
                      startIcon={isSharing && <CircularProgress size={18} />}
                    >
                      {isSharing ? "Sharing..." : "Share"}
                    </Button>
                  </span>
                </Tooltip>
                
              </Box>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </ThemeProvider>
  )
}

export default SharePointTreeView

type CustomTreeItemProps = {
  onItemClick?: (itemId: string) => void
  onItemDoubleClick?: (itemId: string) => void
} & TreeItemProps
const CustomTreeItem: React.FC<CustomTreeItemProps> = ({onItemClick, onItemDoubleClick, ...props}) => {

  const { itemId, label, ...other } = props

  const handleDoubleClick = () => {
    if (itemId) onItemDoubleClick?.(itemId)
  }

  const handleClick = () => {
    if (itemId) onItemClick?.(itemId)
  }

  return (
    <TreeItem
      key={itemId}
      itemId={itemId}
      label={label}
      onDoubleClick={handleDoubleClick}
      onClick={handleClick}
      sx={{
        '& .MuiTreeItem-iconContainer': {
          width: '8px',
        },
        '& .MuiTreeItem-content': {
          padding: '4px 8px 4px 0 !important',
          gap: '0 !important',
        },
        '&, & .MuiTreeItem-label:hover, & .MuiTreeItem-content:hover, & .MuiTreeItem-root:hover': {
          cursor: 'default',
          userSelect: 'none',
        },
      }}
      {...other}
    />
  )
}
